import { GuaranteeType, QuoteFlow } from 'store/modules/conlicitaseg_store/types';

export type QuoteStep = 'motivo' | 'upload' | 'tomador' | 'licitacao' | 'confirmacao';

export interface QuoteFlowConfig {
  reasonStepTitle: string | null;
  reasonPageWithNotices: boolean;
  introVariant: 'bidding' | 'dashboard' | 'none';
  typeSelectionInUpload: boolean;
  holderTitle: string;
  participationHeader: boolean;
  prefillPolicyholderFromProfile: boolean;
  showLinkedCnpjNotice: boolean;
  contractInfoSubtitle: string;
}

const DASHBOARD_CONFIG: QuoteFlowConfig = {
  reasonStepTitle: 'Motivo do seguro',
  reasonPageWithNotices: false,
  introVariant: 'dashboard',
  typeSelectionInUpload: false,
  holderTitle: 'Certo, agora vamos para as informações do Tomador e do Segurado',
  participationHeader: false,
  prefillPolicyholderFromProfile: false,
  showLinkedCnpjNotice: false,
  contractInfoSubtitle: 'Informe abaixo os dados relacionados a esta licitação.',
};

export const QUOTE_FLOW_CONFIG: Record<QuoteFlow, QuoteFlowConfig> = {
  bidding: {
    reasonStepTitle: null,
    reasonPageWithNotices: false,
    introVariant: 'bidding',
    typeSelectionInUpload: true,
    holderTitle: 'Informações do Tomador e Segurado',
    participationHeader: true,
    prefillPolicyholderFromProfile: true,
    showLinkedCnpjNotice: true,
    contractInfoSubtitle: 'Informe abaixo os dados relacionados a licitação.',
  },
  dashboard: DASHBOARD_CONFIG,
  dashboard_inactive: {
    ...DASHBOARD_CONFIG,
    reasonStepTitle: 'Tipo de garantia',
    reasonPageWithNotices: true,
    introVariant: 'none',
  },
  fidelize: {
    ...DASHBOARD_CONFIG,
    reasonStepTitle: null,
    introVariant: 'none',
    typeSelectionInUpload: true,
    prefillPolicyholderFromProfile: true,
    showLinkedCnpjNotice: true,
  },
};

const FIDELIZE_WITHOUT_UPLOAD_CONFIG: QuoteFlowConfig = {
  ...QUOTE_FLOW_CONFIG.fidelize,
  reasonStepTitle: 'Tipo de garantia',
  reasonPageWithNotices: true,
  typeSelectionInUpload: false,
};

export const getQuoteFlowConfig = (flow: QuoteFlow, canUploadContract: boolean): QuoteFlowConfig =>
  flow === 'fidelize' && !canUploadContract
    ? FIDELIZE_WITHOUT_UPLOAD_CONFIG
    : QUOTE_FLOW_CONFIG[flow];

const QUOTE_BASE_PATH = '/conlicitaseg/garantia';

const STEP_TITLES: Record<Exclude<QuoteStep, 'motivo'>, string> = {
  upload: 'Upload do Contrato',
  tomador: 'Tomador e Segurado',
  licitacao: 'Licitação e Contrato',
  confirmacao: 'Confirmação dos dados',
};

export const getDefaultQuoteFlow = (isOnlyConlicitaseg: boolean): QuoteFlow =>
  isOnlyConlicitaseg ? 'dashboard_inactive' : 'dashboard';

export const isExecucao = (typeWarrantyId: string, guaranteeType: GuaranteeType): boolean =>
  guaranteeType ? guaranteeType === 'execucao' : !!typeWarrantyId && typeWarrantyId !== 'Licitante';

export const getQuoteStepPath = (step: QuoteStep): string => {
  switch (step) {
    case 'motivo':
      return `${QUOTE_BASE_PATH}/motivo`;
    case 'upload':
      return `${QUOTE_BASE_PATH}/upload-contrato`;
    case 'tomador':
      return `${QUOTE_BASE_PATH}/tomador-segurado`;
    case 'licitacao':
      return `${QUOTE_BASE_PATH}/licitacao-contrato`;
    default:
      return `${QUOTE_BASE_PATH}/confirmacao`;
  }
};

export const LEGACY_QUOTE_PATHS: Record<string, QuoteStep> = {
  [`${QUOTE_BASE_PATH}/tomador-sem-contrato`]: 'tomador',
  [`${QUOTE_BASE_PATH}/dados-tomador-segurado`]: 'tomador',
  [`${QUOTE_BASE_PATH}/dados-licitacao-contrato`]: 'licitacao',
};

export const getQuoteSteps = (
  flow: QuoteFlow,
  execucao: boolean,
  canUploadContract: boolean,
): QuoteStep[] => {
  const steps: QuoteStep[] = [];
  if (getQuoteFlowConfig(flow, canUploadContract).reasonStepTitle) steps.push('motivo');
  if (execucao && canUploadContract) steps.push('upload');
  return [...steps, 'tomador', 'licitacao', 'confirmacao'];
};

export const getStepIndicatorProps = (
  flow: QuoteFlow,
  currentStep: QuoteStep,
  execucao: boolean,
  canUploadContract: boolean,
) => {
  const { reasonStepTitle } = getQuoteFlowConfig(flow, canUploadContract);
  const steps = getQuoteSteps(flow, execucao, canUploadContract);

  return {
    currentStep: Math.max(steps.indexOf(currentStep), 0),
    steps: steps.map((step) => ({
      title: step === 'motivo' ? reasonStepTitle || '' : STEP_TITLES[step],
    })),
    links: steps.map((step) => getQuoteStepPath(step)),
  };
};

export const getFlowEntryPath = (flow: QuoteFlow, canUploadContract: boolean): string | null => {
  const config = getQuoteFlowConfig(flow, canUploadContract);
  if (config.introVariant !== 'none') return null;
  return getQuoteStepPath(config.reasonStepTitle ? 'motivo' : 'upload');
};
